/**
 * MaterialQuickAdd – Inline aanmaken van UniverseelMateriaal (plaatmateriaal).
 * Gebruikt in Deuren-tab Onzichtbare Scharnieren bij lege materiaallijst.
 * Behoudt parent form state; na succesvol aanmaken wordt het nieuwe item geselecteerd.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '../../services/api'
import toast from 'react-hot-toast'
import { useTouchedGuard } from '../../hooks/useUnsavedChanges'

/**
 * @param {Object} props
 * @param {boolean} props.open - Modal open state
 * @param {function} props.onClose - Sluit modal
 * @param {function} props.onCreated - (materiaal: {id, naam, dikte_mm, dichtheid_kg_m3, ...}) => void – selecteer nieuw item
 * @param {boolean} [props.suitableForDeuren] - Default categorie SHEETS voor deur-materialen filter
 */
export function MaterialQuickAdd({ open, onClose, onCreated, suitableForDeuren = true }) {
  const { t } = useTranslation()
  const guard = useTouchedGuard(open)
  const queryClient = useQueryClient()
  const [form, setForm] = useState({
    naam: '',
    materiaal_groep_id: null,
    dikte_mm: 18,
    categorie: 'SHEETS',
    eenheid: 'm²',
  })

  const { data: groepen = [] } = useQuery({
    queryKey: ['materiaal-groepen'],
    queryFn: async () => {
      const res = await api.get('/materiaal-groepen')
      return res.data || []
    },
    enabled: open,
  })

  const createMutation = useMutation({
    mutationFn: async (data) => {
      const payload = {
        naam: data.naam,
        materiaal_groep_id: data.materiaal_groep_id,
        materiaal_groep_ids: data.materiaal_groep_id ? [data.materiaal_groep_id] : undefined,
        eenheid: data.eenheid || 'm²',
        categorie: suitableForDeuren ? 'SHEETS' : (data.categorie || null),
        standard_thickness_mm: data.dikte_mm ? Number(data.dikte_mm) : null,
        is_actief: true,
      }
      const res = await api.post('/materialen', payload)
      return res.data
    },
    onSuccess: (created) => {
      queryClient.invalidateQueries(['materialen', 'deur-materialen'])
      queryClient.invalidateQueries(['materialen'])
      const item = {
        id: created.id,
        naam: created.naam,
        dikte_mm: created.standard_thickness_mm ?? created.dikte_mm,
        dichtheid_kg_m3: 720,
      }
      onCreated?.(item)
      onClose?.()
      toast.success(t('doors.materialCreated', 'Materiaal succesvol aangemaakt'))
      setForm({ naam: '', materiaal_groep_id: null, dikte_mm: 18, categorie: 'SHEETS', eenheid: 'm²' })
    },
    onError: (err) => {
      toast.error(err?.response?.data?.detail || t('doors.materialCreateFailed', 'Fout bij aanmaken materiaal'))
    },
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!form.naam?.trim()) {
      toast.error(t('doors.materialNameRequired', 'Naam is verplicht'))
      return
    }
    const groepId = form.materiaal_groep_id ?? groepen[0]?.id
    if (!groepId) {
      toast.error(t('doors.materialGroupRequired', 'Selecteer een materiaalgroep'))
      return
    }
    createMutation.mutate({ ...form, materiaal_groep_id: groepId })
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50" onClick={() => guard.closeWith(onClose)}>
      <div
        className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6"
        onClick={(e) => e.stopPropagation()}
        onChange={guard.onContentChange}
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          {t('doors.quickAddMaterialTitle', 'Nieuw plaatmateriaal aanmaken')}
        </h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('doors.materialName', 'Naam')} *</label>
            <input
              type="text"
              value={form.naam}
              onChange={(e) => setForm((p) => ({ ...p, naam: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              placeholder={t('doors.materialNamePlaceholder', 'bijv. MDF 18mm wit')}
              autoFocus
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('doors.materialGroup', 'Materiaalgroep')} *</label>
            <select
              value={form.materiaal_groep_id ?? ''}
              onChange={(e) => setForm((p) => ({ ...p, materiaal_groep_id: e.target.value ? parseInt(e.target.value, 10) : null }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">{t('doors.selectGroup', 'Selecteer groep...')}</option>
              {groepen.map((g) => (
                <option key={g.id} value={g.id}>{g.naam}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('doors.thicknessMm', 'Dikte (mm)')}</label>
            <input
              type="number"
              min={6}
              max={50}
              step={1}
              value={form.dikte_mm}
              onChange={(e) => setForm((p) => ({ ...p, dikte_mm: Number(e.target.value) || 18 }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          {suitableForDeuren && (
            <p className="text-xs text-gray-500">
              {t('doors.suitableForDoorsHint', 'Dit materiaal wordt standaard als geschikt voor deuren aangemerkt.')}
            </p>
          )}
          <div className="flex gap-2 justify-end pt-2">
            <button type="button" onClick={() => guard.closeWith(onClose)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
              {t('common.cancel', 'Annuleren')}
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {createMutation.isPending ? t('common.saving', 'Opslaan...') : t('doors.createMaterial', 'Aanmaken')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
